import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Button from "@/components/base/Button";
import { Input } from "@/components/base/Field";
import { useToast } from "@/components/base/Toast";
import { createEvaluation, updateEvaluation, type Evaluation } from "@/services/gradeService";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Évaluation modifiée ; absente pour une création. */
  editing: Evaluation | null;
  periodId: string;
  classId: string;
  className: string;
  /** Barème du cycle : toutes les matières sont notées sur cette valeur. */
  gradeScale: number;
  /** Date proposée (dans la période). */
  defaultDate: string;
}

const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

/** Titre proposé d'après la date : « Évaluation d'octobre », « Évaluation de novembre ». */
function suggestedTitle(date: string): string {
  const month = MONTHS[Number(date.slice(5, 7)) - 1];
  if (!month) return "";
  return /^[aeiouy]/.test(month) ? `Évaluation d'${month}` : `Évaluation de ${month}`;
}

/** Créer ou modifier une évaluation de la classe : elle porte sur toutes les matières de la classe. */
export default function EvaluationFormModal({ open, onClose, onSaved, editing, periodId, classId, className, gradeScale, defaultDate }: Props) {
  const { notify } = useToast();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(editing?.title ?? suggestedTitle(defaultDate));
    setDate(editing?.date ?? defaultDate);
  }, [open, editing, defaultDate]);

  const save = async () => {
    if (!title.trim()) return notify("Donnez un nom à l'évaluation (ex. « Évaluation d'octobre »).", "error");
    if (!date) return notify("Indiquez la date de l'évaluation.", "error");
    setSaving(true);
    try {
      if (editing) {
        await updateEvaluation(editing.id, { title: title.trim(), date });
        notify("Évaluation modifiée.");
      } else {
        await createEvaluation({ periodId, classId, title: title.trim(), date });
        notify(`Évaluation créée pour toutes les matières de la ${className}.`);
      }
      onSaved();
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Enregistrement impossible.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? "Modifier l'évaluation" : `Nouvelle évaluation — ${className}`}
      subtitle={`Elle porte sur toutes les matières de la classe, notées sur ${gradeScale}`}
      icon="ri-file-list-3-line"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button icon="ri-save-3-line" onClick={save} disabled={saving}>
            {saving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input label="Nom de l'évaluation" placeholder="ex. Évaluation d'octobre" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} />
        <Input
          label="Date"
          type="date"
          value={date}
          onChange={(e) => {
            // Le nom proposé suit la date tant que l'utilisateur ne l'a pas changé.
            if (!editing && title === suggestedTitle(date)) setTitle(suggestedTitle(e.target.value));
            setDate(e.target.value);
          }}
        />
      </div>
      <p className="mt-3 text-xs text-foreground-500">
        La note du trimestre dans chaque matière est la moyenne des évaluations du trimestre.
      </p>
    </Modal>
  );
}
